import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  RegistryLabelingStrategy,
  SchemaInterfaceExtendingSchema,
  TagSchema,
} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ItemListingInterfacesQuery} from 'nav/sidepanel/listing/__generated/ItemListingInterfacesQuery';
import {ITEM_LISTING_INTERFACES_QUERY} from 'nav/sidepanel/listing/ItemListing';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {getDefaultLabelingStrategy} from 'registry/labeling-strategies/LabelingStrategiesUtil';
import LabelingStrategyInput from 'registry/labeling-strategies/LabelingStrategyInput';
import * as RegistryUtil from 'registry/RegistryUtil';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import SchemaSelector from 'samples/SchemaSelector';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from 'schemas/schema-interface/fields/CustomSchemaInterfaceFieldsUtils';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';

import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {SchemaInterfaceWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/schema-interfaces/__generated/SchemaInterfaceWithFieldsGraphQL';
import {
  AutomationOutputFileEntitySchemaInfo,
  ClientAutomationOutputFileConfigSpec,
  INVENTORY_ACTION_TYPES,
  TagSchemaInfoActionType,
  TransformationFieldInfo,
  TransformationFieldType,
} from '../RunSchemaSpecConstants';
import SchemaInfoFieldsSection from './SchemaInfoFieldsSection';
import {openSetColumnsMapModal} from './SetColumnsMapEditor';

const ACTION_TYPE_DESCRIPTIONS: {[action in TagSchemaInfoActionType]: string | null} = {
  CREATE: 'Will error if an entity with the same name already exists',
  UPDATE: 'Will error if an entity with given name cannot be found',
  CREATE_OR_UPDATE: null,
};

export function ActionTypeSelector(props: {
  entitySchemaInfo: AutomationOutputFileEntitySchemaInfo;
  onSelect: (value: TagSchemaInfoActionType) => void;
}): JSX.Element {
  return (
    <>
      <SelectDropdown
        className={css(styles.actionTypeSelector)}
        displayName={entitySchemaInfoActionOptions[props.entitySchemaInfo.actionType]}
        menu={
          <Menu
            value={props.entitySchemaInfo.actionType}
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            scrollable={true}
          >
            {Object.values(TagSchemaInfoActionType).map((actionType) => (
              <Option key={actionType} value={actionType} onSelect={props.onSelect}>
                {entitySchemaInfoActionOptions[actionType]}
              </Option>
            ))}
          </Menu>
        }
      />
      <span className={css(styles.actionTypeDescription)}>
        {ACTION_TYPE_DESCRIPTIONS[props.entitySchemaInfo.actionType]}
      </span>
    </>
  );
}

export interface EntitySchemaInfoModalProps {
  baseSchema: AssayRunSchema | null;
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  ownerId: string;
}

export const entitySchemaInfoActionOptions: {[action in TagSchemaInfoActionType]: string} = {
  CREATE: 'Register new entities',
  UPDATE: 'Update existing entities',
  CREATE_OR_UPDATE: 'Update entity if name already exists; otherwise, register new entities',
};

export default function EntitySchemaInfoModal(props: EntitySchemaInfoModalProps): JSX.Element {
  const tagSchemasQueryResult = useBenchlingQuery<AllTagSchemasQuery>(ALL_TAG_SCHEMAS_QUERY);
  const fieldsetsQueryResult = useBenchlingQuery<ItemListingInterfacesQuery>(ITEM_LISTING_INTERFACES_QUERY);
  const [outputFileConfig, setOutputFileConfig] = useState(props.outputFileConfig);
  const entitySchemaInfo = outputFileConfig.entitySchemaInfo!;
  const setEntitySchemaInfo = (newEntitySchemaInfo: AutomationOutputFileEntitySchemaInfo): void => {
    setOutputFileConfig({...outputFileConfig, entitySchemaInfo: newEntitySchemaInfo});
  };
  let allTagSchemas: Array<TagSchemaListingWithFieldsGraphQL> = [];
  let allFieldsets: Array<SchemaInterfaceExtendingSchema> = [];
  let selectedSchema: TagSchemaListingWithFieldsGraphQL | null = null;
  if (tagSchemasQueryResult.type === QueryResultType.SUCCESS) {
    allTagSchemas = tagSchemasQueryResult.data.tagSchemas;
    selectedSchema = entitySchemaInfo.schema
      ? (allTagSchemas.find((schema) => schema.id === entitySchemaInfo.schema) ?? null)
      : null;
  }
  if (fieldsetsQueryResult.type === QueryResultType.SUCCESS) {
    const fieldsetsFromQuery = fieldsetsQueryResult.data.schemaInterfaces;
    allFieldsets = fieldsetsFromQuery.map((fieldset: SchemaInterfaceWithFieldsGraphQL) =>
      mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(fieldset)
    );
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onUpdateConfig(outputFileConfig);
        props.onRequestSubmit();
      }}
      title="Configure entity schema"
      buttons={[
        {
          text: 'Create new entity schema',
          submit: false,
          style: entitySchemaInfo.schema == null ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_BLUE,
          onClick: () => {
            openSetColumnsMapModal({
              ...props,
              onUpdateConfig: setOutputFileConfig,
            });
          },
        },
        {
          text: 'Done',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: entitySchemaInfo.schema == null,
        },
      ]}
      footer={
        INVENTORY_ACTION_TYPES.has(props.outputFileConfig.actionType) ? (
          <CheckboxInput
            id="should-replace-existing"
            checked={entitySchemaInfo.shouldReplaceExisting || false}
            label="Replace existing entities in containers"
            onChange={(e) => {
              setEntitySchemaInfo({
                ...entitySchemaInfo,
                shouldReplaceExisting: e.target.checked,
              });
            }}
          />
        ) : null
      }
    >
      {tagSchemasQueryResult.type === QueryResultType.SUCCESS ? (
        <>
          <FieldLabel label="Action type" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <br />
          <ActionTypeSelector
            entitySchemaInfo={entitySchemaInfo}
            onSelect={(value) => {
              setEntitySchemaInfo({
                ...entitySchemaInfo,
                actionType: value,
              });
            }}
          />
          <div className={css(styles.row)}>
            <div className={css(styles.entitySchemaInput)}>
              <FieldLabel label="Entity schema" labelStyle={LabelStyle.LABEL} isRequired={true} />
              <br />
              <SchemaSelector
                isDisabled={false}
                onChange={(schema: TagSchema) => {
                  setEntitySchemaInfo({
                    ...entitySchemaInfo,
                    schema: schema.id,
                    fields: {},
                    labelingStrategy: getDefaultLabelingStrategy(schema.labelingStrategies, [schema.id]),
                  });
                }}
                schemas={allTagSchemas}
                selectedSchemaId={entitySchemaInfo.schema}
              />
            </div>
            <div className={css(styles.folderInput)}>
              <FieldLabel label="Default folder" labelStyle={LabelStyle.LABEL} />
              <br />
              <FolderSelector
                buttonSize={ButtonSize.SMALL}
                enableRedesign={true}
                allowNone={true}
                showInventory={true}
                showRegistries={true}
                value={entitySchemaInfo.folder}
                onChange={(e: {target: {value: string}}) => {
                  const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
                  if (folderId != null) {
                    setEntitySchemaInfo({...entitySchemaInfo, folder: folderId});
                  } else {
                    const newEntitySchemaInfo = {...entitySchemaInfo};
                    delete newEntitySchemaInfo.folder;
                    setEntitySchemaInfo(newEntitySchemaInfo);
                  }
                }}
                unsetOnFetchFail={true}
              />
            </div>
          </div>
          <div className={css(styles.row)}>
            <div className={css(styles.labelingStrategyInput)}>
              <LabelingStrategyInput
                disabled={selectedSchema == null}
                labelingStrategy={entitySchemaInfo.labelingStrategy}
                labelingStrategies={selectedSchema ? selectedSchema.labelingStrategies : []}
                onChange={(labelingStrategy: RegistryLabelingStrategy) => {
                  setEntitySchemaInfo({...entitySchemaInfo, labelingStrategy});
                }}
                tagSchemaIds={entitySchemaInfo.schema ? [entitySchemaInfo.schema] : []}
              />
            </div>
          </div>
          <SchemaInfoFieldsSection
            schema={selectedSchema}
            fields={entitySchemaInfo.fields}
            allTagSchemas={allTagSchemas}
            allFieldsets={allFieldsets}
            allowedFieldTypes={[
              TransformationFieldType.DIRECT,
              TransformationFieldType.SOURCE_CONTENTS,
              TransformationFieldType.CALCULATE_FINAL_CONCENTRATION,
            ]}
            onUpdateField={(fieldName: string, fieldInfo: TransformationFieldInfo | null) => {
              const newFields = {...entitySchemaInfo.fields};
              if (fieldInfo) {
                newFields[fieldName] = fieldInfo;
              } else {
                delete newFields[fieldName];
              }
              setEntitySchemaInfo({...entitySchemaInfo, fields: newFields});
            }}
          />
        </>
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: '20px',
  },
  entitySchemaInput: {
    flex: 0.5,
    marginRight: '40px',
  },
  folderInput: {
    flex: 0.5,
  },
  labelingStrategyInput: {
    flexGrow: 1,
  },
  actionTypeSelector: {
    marginBottom: '5px',
  },
  actionTypeDescription: {
    fontSize: 13,
    lineHeight: '16px',
    color: GRAY_DARKER,
  },
});
